/* Portal Users panel — lets dispatch review customer self-service signups.
   Shows each portal account with linked Customer, status, and quick actions
   (approve / disable / merge into existing customer / delete). Lives inside
   the staff Customers page as its own tab. */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Link2, Trash2, UserPlus, RefreshCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { formatLocalDateTime } from "@/lib/dates";

const STATUS_STYLES = {
  pending:  "border-primary-orange text-primary-orange bg-primary-orange/10",
  active:   "border-emerald-500/60 text-emerald-400 bg-emerald-500/10",
  disabled: "border-neutral-600 text-muted-industrial bg-neutral-800/40",
};

export default function PortalUsersPanel({ customers }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");   // all | pending | active | disabled
  const [query, setQuery] = useState("");
  const [linkTarget, setLinkTarget] = useState(null); // { user, customer_id }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal-users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load portal users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== "all" && (u.status || "active") !== filter) return false;
      if (!q) return true;
      return (
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.customer_name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q)
      );
    });
  }, [users, filter, query]);

  const counts = useMemo(() => {
    const c = { pending: 0, active: 0, disabled: 0 };
    users.forEach((u) => { c[u.status || "active"] = (c[u.status || "active"] || 0) + 1; });
    return c;
  }, [users]);

  const approve = async (u) => {
    try {
      await api.post(`/portal-users/${u.id}/approve`);
      toast.success(`Approved ${u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const disable = async (u) => {
    if (!window.confirm(`Disable ${u.email}? They will no longer be able to log in.`)) return;
    try {
      await api.post(`/portal-users/${u.id}/disable`);
      toast.success(`Disabled ${u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Permanently delete portal login ${u.email}?`)) return;
    try {
      await api.delete(`/portal-users/${u.id}`);
      toast.success("Portal user deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doLink = async () => {
    if (!linkTarget?.customer_id) return;
    try {
      await api.post(`/portal-users/${linkTarget.user.id}/link-customer`, {
        target_customer_id: linkTarget.customer_id,
        delete_stub: true,
      });
      toast.success("Portal user re-linked to customer");
      setLinkTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const chip = (key, label) => (
    <button
      key={key}
      data-testid={`portal-filter-${key}`}
      onClick={() => setFilter(key)}
      className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
        filter === key
          ? "border-primary-orange text-primary-orange bg-primary-orange/10"
          : "border-industrial text-muted-industrial hover:text-ink"
      }`}
    >
      {label}
      {key !== "all" && (
        <span className="ml-1.5 text-muted-industrial">· {counts[key] || 0}</span>
      )}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="portal-users-panel">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          {chip("all", "All")}
          {chip("pending", "Pending review")}
          {chip("active", "Active")}
          {chip("disabled", "Disabled")}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-industrial" />
            <Input
              data-testid="portal-user-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email, name, customer…"
              className="rounded-none bg-transparent border-industrial mono text-xs pl-8 h-9 w-64"
            />
          </div>
          <button
            data-testid="portal-users-refresh"
            onClick={load}
            className="text-muted-industrial hover:text-primary-orange p-2 border border-industrial"
            title="Refresh"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial">
            <tr className="text-left">
              <th className="p-3 label-tag">Portal Login</th>
              <th className="p-3 label-tag">Linked Customer</th>
              <th className="p-3 label-tag">Signed Up</th>
              <th className="p-3 label-tag">Last Login</th>
              <th className="p-3 label-tag">Status</th>
              <th className="p-3 label-tag text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-industrial mono text-xs uppercase tracking-widest">
                  {loading ? "Loading portal users…" : "No portal users match this filter"}
                </td>
              </tr>
            )}
            {filtered.map((u) => {
              const s = u.status || "active";
              return (
                <tr key={u.id} data-testid={`portal-user-row-${u.id}`}
                    className="border-b border-industrial hover:bg-surface-2">
                  <td className="p-3">
                    <div className="font-semibold">{u.name || u.email}</div>
                    <div className="text-xs text-muted-industrial mono">{u.email}</div>
                    {u.phone && <div className="text-xs text-muted-industrial mono">{u.phone}</div>}
                  </td>
                  <td className="p-3">
                    <div className="text-sm">{u.customer_name || <span className="text-muted-industrial">Unlinked</span>}</div>
                    <div className="text-xs text-muted-industrial mono uppercase tracking-widest">
                      {u.customer_type === "account"
                        ? `Account · ${u.credit_terms_days}d`
                        : "EFT sale"}
                    </div>
                  </td>
                  <td className="p-3 mono text-xs text-muted-industrial">
                    {formatLocalDateTime(u.created_at)}
                  </td>
                  <td className="p-3 mono text-xs text-muted-industrial">
                    {u.last_login_at ? formatLocalDateTime(u.last_login_at) : <span className="text-muted-industrial">—</span>}
                  </td>
                  <td className="p-3">
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_STYLES[s] || STATUS_STYLES.active}`}>
                      {s}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      {s === "pending" && (
                        <button
                          data-testid={`portal-approve-${u.id}`}
                          onClick={() => approve(u)}
                          title="Approve · mark active"
                          className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10"
                        >
                          <CheckCircle2 className="w-3 h-3" />Approve
                        </button>
                      )}
                      <button
                        data-testid={`portal-link-${u.id}`}
                        onClick={() => setLinkTarget({ user: u, customer_id: "" })}
                        title="Link to an existing customer"
                        className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-primary-orange hover:text-primary-orange"
                      >
                        <Link2 className="w-3 h-3" />Link
                      </button>
                      {s !== "disabled" && (
                        <button
                          data-testid={`portal-disable-${u.id}`}
                          onClick={() => disable(u)}
                          title="Disable login"
                          className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-red-500 hover:text-red-400"
                        >
                          <XCircle className="w-3 h-3" />Disable
                        </button>
                      )}
                      <button
                        data-testid={`portal-delete-${u.id}`}
                        onClick={() => del(u)}
                        title="Delete portal login"
                        className="text-muted-industrial hover:text-primary-orange p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Link-to-existing-customer dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(v) => { if (!v) setLinkTarget(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="portal-link-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary-orange" />
              Link Portal Login
            </DialogTitle>
          </DialogHeader>
          {linkTarget && (
            <div className="space-y-4">
              <div className="border border-industrial p-3 text-xs mono">
                <div className="label-tag mb-1">Portal Login</div>
                <div className="text-sm font-semibold">{linkTarget.user.email}</div>
                <div className="text-muted-industrial">
                  Currently linked to <span className="text-ink">{linkTarget.user.customer_name || "—"}</span>
                </div>
              </div>
              <div>
                <Label className="label-tag">Merge into existing customer</Label>
                <select
                  data-testid="portal-link-target-select"
                  value={linkTarget.customer_id}
                  onChange={(e) => setLinkTarget({ ...linkTarget, customer_id: e.target.value })}
                  className="w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2"
                >
                  <option value="">— Select customer —</option>
                  {(customers || [])
                    .filter((c) => c.id !== linkTarget.user.customer_id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.customer_type === "account" ? "Account" : "Cash"}
                      </option>
                    ))}
                </select>
                <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-2">
                  The auto-created stub customer will be removed if it has no dockets or invoices.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="portal-link-confirm"
              onClick={doLink}
              disabled={!linkTarget?.customer_id}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
            >
              Confirm Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
